// Supervisory-visit cadence rules per corrected Policy A-3.7 (2026-08-02
// GAPP/HFRD research pass). GAPP clients carry two concurrent requirements
// (PSS/family-caregiver visits and RN assessment of LPN-delivered nursing) —
// non-GAPP clients have exactly one, chosen by care type. Clients who receive
// supplies only carry none: there is no caregiver in the home to supervise.

import { isGapp, isDmeOnly } from './serviceModel';

export const CADENCE = {
  gapp_pss_biweekly: {
    key: 'gapp_pss_biweekly',
    label: 'GAPP — PSS/family caregiver (biweekly)',
    intervalDays: 14,
    citation: 'GAPP Provider Manual §919, §901.17.4.2',
  },
  gapp_rn_monthly: {
    key: 'gapp_rn_monthly',
    label: 'GAPP — RN assessment of LPN-delivered nursing (monthly)',
    intervalDays: 30,
    citation: 'GAPP Provider Manual §912.1.11',
  },
  companion_sitter: {
    key: 'companion_sitter',
    label: 'Companion/Sitter tasks',
    intervalDays: 122,
    citation: 'Ga. Comp. R. & Regs. R. 111-8-65-.10',
  },
  personal_care: {
    key: 'personal_care',
    label: 'Personal care tasks',
    intervalDays: 92,
    citation: 'Ga. Comp. R. & Regs. R. 111-8-65-.10',
  },
};

// Maps a completed visit_type to the cadence rule that governs its "next
// due" recurrence. initial_visit/unannounced/follow_up are supplementary —
// they don't drive their own recurring cadence, so they're left out here.
export const VISIT_TYPE_CADENCE_KEY = {
  gapp_pss_biweekly: 'gapp_pss_biweekly',
  gapp_rn_monthly: 'gapp_rn_monthly',
  scheduled: null, // resolved dynamically from the client's single non-GAPP cadence
};

// Returns the array of cadence rules applicable to a client. GAPP clients
// get both gapp_pss_biweekly and gapp_rn_monthly (independent, concurrent
// tracks). Non-GAPP clients get exactly one rule.
export function getSupervisoryCadences(client) {
  if (!client) return [CADENCE.personal_care];

  // Supplies only — nobody delivers care in the home, so nothing to supervise.
  if (isDmeOnly(client)) return [];

  if (isGapp(client)) {
    return [CADENCE.gapp_pss_biweekly, CADENCE.gapp_rn_monthly];
  }

  if (client.companion_sitter_only) return [CADENCE.companion_sitter];
  return [CADENCE.personal_care];
}

// Resolves the single cadence rule governing a non-GAPP client (used when
// visit_type === 'scheduled', which only ever applies to non-GAPP clients —
// GAPP clients use the two gapp_* visit types instead, each mapping directly
// to its own CADENCE entry above).
export function getNonGappCadence(client) {
  if (client?.companion_sitter_only) return CADENCE.companion_sitter;
  return CADENCE.personal_care;
}
